import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";

import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Chip,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddIcon from "@mui/icons-material/Add";
import CollectionsIcon from "@mui/icons-material/Collections";

import Button from "src/components/core/Button";
import device from "src/styles/device";
import useDeviceSize from "src/hooks/useDeviceSize";
import { getCollectionsList } from "src/api/collection";
import Main from "src/components/core/Main";

// Styled Components
const DivMuiContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 24px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  justify-content: center;
  align-items: center;

  @media ${device.tabletL} {
    padding: 16px;
    border-radius: 16px;
  }

  @media ${device.mobileL} {
    padding: 12px;
    border-radius: 12px;
  }
`;

const DivMuiInside = styled.div`
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: ${({ mt }) => (mt ? `${mt}px` : "0px")};

  @media ${device.tabletL} {
    flex-direction: column;
    gap: 12px;
  }
`;

const WrapperSearch = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  margin: 0;

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
  }

  @media ${device.mobileL} {
    width: 100%;
  }
`;

const WrapperButton = styled.div`
  display: flex;
  gap: 12px;

  @media ${device.tabletL} {
    width: 100%;
    justify-content: stretch;
  }
`;

const WrapperDataGrid = styled.div`
  height: 600px;
  width: 100%;

  .MuiDataGrid-root {
    border: 1px solid #ffffff;
    border-radius: 16px;
  }

  .MuiDataGrid-columnHeaders {
    background-color: #f5f5f5;
  }

  .MuiDataGrid-cell {
    border-bottom: 1px solid #f0f0f0;
  }

  .MuiDataGrid-row:hover {
    background-color: #f9f9f9;
  }

  @media ${device.mobileL} {
    height: 500px;
  }
`;

const CollectionImage = styled(Avatar)`
  &.MuiAvatar-root {
    width: 100px;
    height: 100px;
    border-radius: 8px;
  }

  @media ${device.mobileL} {
    &.MuiAvatar-root {
      width: 60px;
      height: 60px;
    }
  }
`;

const CollectionList = ({ open }) => {
  const navigate = useNavigate();
  const [deviceWidth] = useDeviceSize();

  const [collectionsList, setCollectionsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const [dataSearch, setDataSearch] = useState({
    keyword: "",
    page: 0,
    pageSize: 10,
  });

  // Fetch collections data
  const fetchCollections = async () => {
    try {
      setIsLoading(true);

      try {
        const data = await getCollectionsList({
          keyword: dataSearch.keyword,
          page: dataSearch.page,
          pageSize: dataSearch.pageSize,
        });

        setCollectionsList(data || []);
        setRowCount(data.length || 0);
        return;
      } catch (apiError) {
        console.warn("API not available, using mock data:", apiError);
      }
    } catch (error) {
      console.error("Error fetching collections:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch.keyword, dataSearch.page]);

  const onSearchButton = () => {
    // Trigger search
    setDataSearch((prev) => ({ ...prev }));
  };

  const handleView = (id) => {
    // Navigate to collection detail page
    navigate(`/collection/detail/${id}`);
  };

  const handleEdit = (id) => {
    // Navigate to collection edit page
    navigate(`/collection/edit/${id}`);
  };

  const handleAdd = () => {
    // Navigate to collection create page
    navigate("/collection/create");
  };

  // Desktop Columns
  const DesktopColumns = [
    {
      field: "display_image",
      headerName: "รูป",
      width: 120,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ width: "100%", py: 1 }}>
          <CollectionImage
            src={params.row.display_image}
            alt={params.row.name}
            variant="rounded"
          >
            <CollectionsIcon />
          </CollectionImage>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "ชื่อคอลเล็กชัน",
      flex: 1,
      minWidth: 200,
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            py: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "#252525",
              lineHeight: 1.2,
            }}
          >
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "description_th",
      headerName: "คำอธิบาย",
      flex: 2,
      minWidth: 300,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            py: 2,
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Typography
            variant="body2"
            title={params.row.description_th} // Tooltip แสดงข้อความเต็ม
            sx={{
              color: "#666666",
              lineHeight: 1.4,
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
              cursor: "help", // แสดงว่าสามารถ hover ดู tooltip ได้
            }}
          >
            {params.row.description_th}
          </Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "วันที่สร้าง",
      width: 140,
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {moment(params.row.createdAt).format("DD/MM/YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "การดำเนินการ",
      width: 120,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          key="view"
          icon={<VisibilityIcon />}
          label="ดู"
          onClick={() => handleView(params.row?._id)}
        />,
        <GridActionsCellItem
          key="edit"
          icon={<EditRoundedIcon />}
          label="แก้ไข"
          onClick={() => handleEdit(params.row?._id)}
        />,
      ],
    },
  ];

  // Mobile Columns
  const MobileColumns = [
    {
      field: "combined",
      headerName: "คอลเล็กชัน",
      flex: 1,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1,
            width: "100%",
          }}
        >
          <CollectionImage
            src={params.row.display_image}
            alt={params.row.name}
            variant="rounded"
          >
            <CollectionsIcon />
          </CollectionImage>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "#252525",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {params.row.name}
            </Typography>

            <Typography
              variant="caption"
              title={params.row.description_th} // Tooltip แสดงข้อความเต็ม
              sx={{
                color: "#666666",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.3,
                wordBreak: "break-word",
                cursor: "help", // แสดงว่าสามารถ hover ดู tooltip ได้
              }}
            >
              {params.row.description_th}
            </Typography>

            <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
              <Chip
                label={`${params.row.productCount} ชิ้น`}
                color="primary"
                size="small"
                variant="outlined"
              />

              <Chip
                label={params.row.status === "active" ? "ใช้งาน" : "ไม่ใช้งาน"}
                color={params.row.status === "active" ? "success" : "default"}
                size="small"
                variant="outlined"
              />

              <Typography variant="caption" color="textSecondary">
                {moment(params.row.createdAt).format("DD/MM/YY")}
              </Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 80,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          key="view"
          icon={<VisibilityIcon />}
          label="ดู"
          onClick={() => handleView(params.row?._id)}
          color="primary"
        />,
        <GridActionsCellItem
          key="edit"
          icon={<EditRoundedIcon />}
          label="แก้ไข"
          onClick={() => handleEdit(params.row?._id)}
          color="primary"
        />,
      ],
    },
  ];

  return (
    <Main open={open}>
      <DivMuiContainer>
        {/* Search and Add Button */}
        <DivMuiInside mt={10}>
          <WrapperSearch>
            <TextField
              fullWidth
              placeholder="ค้นหาคอลเล็กชันจากชื่อ หรือ รายละเอียด"
              className="TextField-without-border-radius"
              sx={{
                borderRadius: "16px",
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={onSearchButton} sx={{ padding: 0 }}>
                    <SearchIcon sx={{ color: "#e04132", cursor: "pointer" }} />
                  </IconButton>
                ),
              }}
              value={dataSearch.keyword}
              onChange={(e) => {
                setDataSearch({ ...dataSearch, keyword: e.target.value });
              }}
              onKeyUp={(e) => (e.code === "Enter" ? onSearchButton() : null)}
            />
          </WrapperSearch>

          <WrapperButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                minWidth: 180,
                height: 52,
                "@media (max-width: 768px)": {
                  flex: 1,
                },
              }}
            >
              เพิ่มคอลเล็กชัน
            </Button>
          </WrapperButton>
        </DivMuiInside>

        {/* Results Count */}
        <Box sx={{ mb: 2 }}>
          <Typography component="span" color="textSecondary">
            คอลเล็กชันทั้งหมด:{" "}
            <Typography
              component="span"
              sx={{ color: "#E13B30", fontWeight: 600 }}
            >
              {rowCount}
            </Typography>{" "}
            รายการ
          </Typography>
        </Box>

        {/* Data Grid */}
        <WrapperDataGrid>
          <DataGrid
            rows={collectionsList}
            getRowId={(row) => row._id}
            columns={deviceWidth <= 768 ? MobileColumns : DesktopColumns}
            loading={isLoading}
            pageSize={dataSearch.pageSize}
            disableSelectionOnClick
            disableColumnResize={deviceWidth <= 768}
            getRowHeight={() => (deviceWidth <= 768 ? 80 : 120)}
            sx={{
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "#f9f9f9",
                },
              },
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
            }}
          />
        </WrapperDataGrid>
      </DivMuiContainer>
    </Main>
  );
};

export default CollectionList;
